/* IntelApps — Discuss Project / Contact Page */
import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import Header, { Arrow } from '../parts/Header.js';
import Footer from '../parts/Footer.js';

const PROJECT_TYPES = ['AI / ML Solution', 'Mobile App', 'Web Application', 'Blockchain/DApp', 'Other'];
const BUDGETS = ['$25k-50k', '$50k-100k', '$100k-250k', '$250k+'];
const TIMELINES = ['ASAP', '1-3 months', '3-6 months', '6+ months'];
const DESTINATION_EMAIL = 'johnnyzhang90107@gmail.com';

export function DiscussProjectPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'IntelApps — Discuss a Project';
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setOpt = (k, v) => () => setForm((f) => ({ ...f, [k]: v }));

  const buildEmailText = (f) => ([
    `Name: ${f.name}`,
    `Email: ${f.email}`,
    `Company: ${f.company || '-'}`,
    `Project Type: ${f.projectType}`,
    `Budget: ${f.budget}`,
    `Timeline: ${f.timeline}`,
    'Content:',
    f.message,
  ].join('\n'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.projectType || !form.budget || !form.timeline) {
      setError('Please select a project type, budget, and timeline.');
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setError('Email is not configured. Please set EmailJS keys in .env and restart the dev server.');
      return;
    }

    setSending(true);
    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          to_email: DESTINATION_EMAIL,
          from_name: form.name,
          reply_to: form.email,
          subject: `New project brief — ${form.name}${form.company ? ` (${form.company})` : ''}`,
          message: buildEmailText(form),
          name: form.name,
          email: form.email,
          company: form.company,
          projectType: form.projectType,
          budget: form.budget,
          timeline: form.timeline,
          content: form.message,
        },
        publicKey,
      );
      setSent(true);
    } catch (err) {
      setError('Failed to send. Please try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header />
      <main>
        <section className="page-hero">
          <div className="container page-hero-grid">
            <div>
              <span className="eyebrow">Contact</span>
              <h1>Let&apos;s build something great.</h1>
              <p className="page-hero-sub">
                Book a free 30-minute discovery call.
                {' '}
                We&apos;ll tell you if we&apos;re a fit — and if we&apos;re not, who is.
              </p>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="contact-grid">
              <div className="contact-why">
                <h2>Why IntelApps?</h2>
                <div className="contact-points">
                  <div className="contact-point">
                    <span className="arrow">→</span>
                    <div>
                      <h4>Senior team, no juniors</h4>
                      <p>Everyone you meet in kickoff is on your project.</p>
                    </div>
                  </div>
                  <div className="contact-point">
                    <span className="arrow">→</span>
                    <div>
                      <h4>Fixed scope, fixed price</h4>
                      <p>No hourly billing. You know exactly what you&apos;re getting.</p>
                    </div>
                  </div>
                  <div className="contact-point">
                    <span className="arrow">→</span>
                    <div>
                      <h4>Production-ready in 12 weeks</h4>
                      <p>From discovery to deployment, on time and on budget.</p>
                    </div>
                  </div>
                </div>
                <div className="contact-direct">
                  <p className="label">Or reach us directly</p>
                  <a href="mailto:developer@intelapps.ai">developer@intelapps.ai</a>
                  <p className="meta">Dover, DE · Remote-friendly</p>
                </div>
              </div>

              <div>
                {sent ? (
                  <div className="contact-card contact-success">
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'var(--emerald)',
                        color: '#fff',
                        margin: '0 auto 20px',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <h2>Got it.</h2>
                    <p>We&apos;ll be in touch within 24 hours.</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="contact-card"
                  >
                    {error && (
                      <div
                        role="alert"
                        style={{
                          marginBottom: 14,
                          padding: '10px 12px',
                          borderRadius: 10,
                          background: 'rgba(239,68,68,0.12)',
                          border: '1px solid rgba(239,68,68,0.25)',
                          color: 'rgba(255,255,255,0.92)',
                        }}
                      >
                        {error}
                      </div>
                    )}
                    <div className="form-grid-2" style={{ marginBottom: 14 }}>
                      <div>
                        <label className="field-label" htmlFor="contact-name">
                          <span>Name</span>
                          <input
                            id="contact-name"
                            required
                            value={form.name}
                            onChange={set('name')}
                            className="field-input"
                            placeholder="Jane Cooper"
                          />
                        </label>
                      </div>
                      <div>
                        <label className="field-label" htmlFor="contact-email">
                          <span>Email</span>
                          <input
                            id="contact-email"
                            required
                            type="email"
                            value={form.email}
                            onChange={set('email')}
                            className="field-input"
                            placeholder="jane@company.com"
                          />
                        </label>
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label className="field-label" htmlFor="contact-company">
                        <span>Company</span>
                        <input
                          id="contact-company"
                          value={form.company}
                          onChange={set('company')}
                          className="field-input"
                          placeholder="Acme Inc."
                        />
                      </label>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div className="field-label">
                        <span>Project type</span>
                      </div>
                      <div className="options-row">
                        {PROJECT_TYPES.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={setOpt('projectType', t)}
                            className={`opt ${form.projectType === t ? 'selected' : ''}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div className="field-label">
                        <span>Budget</span>
                      </div>
                      <div className="options-row">
                        {BUDGETS.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={setOpt('budget', t)}
                            className={`opt ${form.budget === t ? 'selected' : ''}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div className="field-label">
                        <span>Timeline</span>
                      </div>
                      <div className="options-row">
                        {TIMELINES.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={setOpt('timeline', t)}
                            className={`opt ${form.timeline === t ? 'selected' : ''}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <label className="field-label" htmlFor="contact-message">
                        <span>What are you building?</span>
                        <textarea
                          id="contact-message"
                          required
                          rows="4"
                          value={form.message}
                          onChange={set('message')}
                          className="field-input"
                          placeholder="A short description — what it is, who it&apos;s for, what success looks like."
                          style={{ resize: 'vertical', minHeight: 120 }}
                        />
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-accent btn-arrow"
                      style={{ width: '100%', justifyContent: 'center' }}
                      disabled={sending}
                    >
                      {sending ? 'Sending…' : 'Send project brief'} <Arrow size={14} />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default DiscussProjectPage;
